import { hashKey, QueryFunction } from '@tanstack/react-query';
import {
  DocumentChange,
  FirestoreError,
  onSnapshot,
  Query,
  QuerySnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import {
  DiagnosticsChannel,
  isDiagnosticsEnabled,
  logDiagnostic,
} from 'shared/services/diagnostics';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { queryClient } from './query-client';
import {
  recordSubscriptionDocCount,
  registerSubscription,
  unregisterSubscription,
} from './subscription-registry';

const MAX_RETRIES = 3;

const toMillis = (value: unknown): number | null => {
  if (value instanceof Date) {
    return value.getTime();
  }
  const maybeTimestamp = value as { toDate?: () => Date } | null | undefined;
  if (maybeTimestamp && typeof maybeTimestamp.toDate === 'function') {
    return maybeTimestamp.toDate().getTime();
  }
  return null;
};

type SnapshotJobStats = {
  skipped: number;
  converted: number;
  hadRemovals: boolean;
};

const makeJobStats = (): SnapshotJobStats => ({
  skipped: 0,
  converted: 0,
  hadRemovals: false,
});

/**
 * Creates a TanStack Query function that establishes real-time Firestore listeners.
 *
 * 1. Multiplexes several queries under one queryKey and resolves once every query has fired its
 *    initial payload (from cache or server).
 * 2. Processes `docChanges()` deltas — not the full `docs` array — so converter work stays O(Δ):
 *    a doc whose `updatedAt` is unchanged keeps its existing object (identity preserved) and never
 *    runs the (expensive) converter.
 * 3. Tears down listeners via the subscription registry (cache `removed` event) instead of the
 *    queryFn AbortSignal, which never fires post-resolve in TanStack 5.0.0.
 * 4. Prunes stale docs across query sources; the O(N) scan only runs while initializing or when a
 *    job contained `removed` changes.
 * 5. Retries failed listeners with exponential backoff (up to MAX_RETRIES).
 */
export const createSubscriptionQuery = <T extends { id: ID }>(
  queries: (() => Promise<Query<T>>)[],
): QueryFunction<T[] | undefined, string[]> => {
  return async (context) => {
    const { signal, queryKey } = context;
    if (signal?.aborted) {
      throw new Error('Query aborted before execution');
    }

    getErrorReporter().addBreadcrumb({
      category: 'tanstack-query',
      message: JSON.stringify(queryKey),
    });

    const queryHash = hashKey(queryKey);
    const subscribeStartMs = performance.now();
    const queryRefs = await Promise.all(queries.map((query) => query()));

    // Cross-generation identity baseline: a re-executed queryFn reuses the previously cached objects
    // for docs whose updatedAt is unchanged, so the first snapshot doesn't re-convert the whole set.
    const previousById = new Map(
      (queryClient.getQueryData<T[]>(queryKey) ?? []).map((item) => [
        item.id,
        item,
      ]),
    );

    let resolved = false;
    let pruneSettled = false;
    const combinedDataMap = new Map<ID, T>();
    const queryIdSets: Set<ID>[] = queries.map(() => new Set<ID>());
    const initializedQueries = new Set<number>();
    const retryCounts = new Array(queries.length).fill(0);
    const unsubscribers: Unsubscribe[] = new Array(queries.length);

    return new Promise<T[] | undefined>((resolve, reject) => {
      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) {
          return;
        }
        cleanedUp = true;
        unsubscribers.forEach((unsub) => unsub?.());
      };

      registerSubscription(queryHash, cleanup);

      logDiagnostic(DiagnosticsChannel.Listener, () => ({
        event: 'register',
        queryKey,
        queryHash,
      }));

      signal?.addEventListener('abort', () => {
        unregisterSubscription(queryHash, cleanup);
        cleanup();
        if (!resolved) {
          resolved = true;
          reject(new Error('Query aborted by React Query'));
        }
      });

      const updateCacheAndResolve = (jobHadRemovals: boolean) => {
        if (jobHadRemovals || !pruneSettled) {
          const allActiveIds = new Set(
            queryIdSets.flatMap((set) => Array.from(set)),
          );
          for (const [id] of combinedDataMap) {
            if (!allActiveIds.has(id)) {
              combinedDataMap.delete(id);
            }
          }
          if (initializedQueries.size === queryRefs.length) {
            pruneSettled = true;
          }
        }

        const combinedData = Array.from(combinedDataMap.values());

        if (!resolved) {
          if (initializedQueries.size === queryRefs.length) {
            resolved = true;
            resolve(combinedData);
          }
          return;
        }

        queryClient.setQueryData(queryKey, combinedData);
      };

      const processChange = (
        change: DocumentChange<T>,
        queryIds: Set<ID>,
        stats: SnapshotJobStats,
      ) => {
        if (change.type === 'removed') {
          stats.hadRemovals = true;
          queryIds.delete(change.doc.id as ID);
          return;
        }

        const docId = change.doc.id as ID;
        const existing = combinedDataMap.get(docId) ?? previousById.get(docId);
        const existingMillis = toMillis(
          (existing as { updatedAt?: unknown } | undefined)?.updatedAt,
        );
        const incomingMillis = toMillis(change.doc.get('updatedAt'));

        if (
          existing &&
          existingMillis !== null &&
          incomingMillis !== null &&
          existingMillis === incomingMillis
        ) {
          combinedDataMap.set(docId, existing);
          queryIds.add(docId);
          stats.skipped += 1;
          return;
        }

        const data = change.doc.data();
        combinedDataMap.set(data.id, data);
        queryIds.add(data.id);
        stats.converted += 1;
      };

      const setupSingleSubscription = (index: number) => {
        const queryRef = queryRefs[index];

        const onSnapshotSuccess = (snapshot: QuerySnapshot<T>) => {
          // Cached snapshots fire alongside server errors (e.g. missing index); only a server-confirmed
          // success may reset the retry counter, otherwise it can never reach MAX_RETRIES.
          if (!snapshot.metadata.fromCache) {
            retryCounts[index] = 0;
          }

          const processStartMs = isDiagnosticsEnabled() ? performance.now() : 0;
          const isFirstSnapshot = !initializedQueries.has(index);

          const queryIds = queryIdSets[index];
          const changes = snapshot.docChanges();
          const stats = makeJobStats();
          changes.forEach((change) => processChange(change, queryIds, stats));

          initializedQueries.add(index);
          updateCacheAndResolve(stats.hadRemovals);

          recordSubscriptionDocCount(
            queryHash,
            String(queryKey[0] ?? 'unknown'),
            combinedDataMap.size,
          );

          logDiagnostic(
            isFirstSnapshot
              ? DiagnosticsChannel.FirstSnapshot
              : DiagnosticsChannel.Snapshot,
            () => ({
              queryKey,
              index,
              // The load-bearing split: cache-slow points at the local cache,
              // server-slow at the network / a missing index.
              fromCache: snapshot.metadata.fromCache,
              hasPendingWrites: snapshot.metadata.hasPendingWrites,
              changes: changes.length,
              skipped: stats.skipped,
              converted: stats.converted,
              docsInSnapshot: snapshot.size,
              docsHeld: combinedDataMap.size,
              processMs: Math.round(performance.now() - processStartMs),
              ...(isFirstSnapshot
                ? {
                    sinceSubscribeMs: Math.round(
                      performance.now() - subscribeStartMs,
                    ),
                  }
                : {}),
            }),
          );
        };

        const onSnapshotError = (error: FirestoreError) => {
          if (!isProductionEnvironment) {
            const errorContext = `index=${index} retry=${retryCounts[index]} at ${performance.now().toFixed(0)}ms`;
            // eslint-disable-next-line no-console -- non-prod logging
            console.warn(
              `[Debug-Subscription] ERROR query ${JSON.stringify(queryKey)} ${errorContext}`,
              error,
            );
          }
          // cleanup THIS specific listener (it's dead anyway, but good hygiene)
          unsubscribers[index]?.();

          // create a new listener for this specific query if we haven't reached the max retries
          if (retryCounts[index] < MAX_RETRIES) {
            retryCounts[index]++;
            const delay = 1000 * retryCounts[index]; // Exponential backoff: 1s, 2s, 3s...

            // wait, then try to subscribe again
            setTimeout(() => {
              // A superseded/torn-down generation must not resurrect a listener nobody owns
              if (!signal?.aborted && !cleanedUp) {
                setupSingleSubscription(index);
              }
            }, delay);
            return;
          }

          if (!isProductionEnvironment) {
            // eslint-disable-next-line no-console -- non-prod logging
            console.log('Firestore Query Error', { queryKey, index, error });
          }

          getErrorReporter().captureException(error, {
            tags: {
              source: 'firestore-subscription-query',
              queryKey: JSON.stringify(queryKey),
              queryIndex: String(index),
            },
            contexts: {
              subscriptionQuery: { queryKey, index },
            },
          });

          unregisterSubscription(queryHash, cleanup);
          cleanup();

          // if max tries have been reached, time for cleanup and rejection
          if (!resolved) {
            resolved = true;
            reject(error);
            return;
          }
          // Set the query to error state so the UI can react accordingly.
          // Don't use invalidateQueries — it re-executes the queryFn,
          // resetting retry counters in a new closure and creating an infinite loop.
          const cachedQuery = queryClient.getQueryCache().get(queryHash);
          if (cachedQuery) {
            // query.setState is not documented by TanStack Query but is used
            // internally to set error state. There is no public API for this.
            cachedQuery.setState({
              status: 'error',
              error,
              fetchStatus: 'idle',
            });
          }
        };

        // connect and store the specific unsub function
        unsubscribers[index] = onSnapshot(
          queryRef,
          onSnapshotSuccess,
          onSnapshotError,
        );
      };

      try {
        queryRefs.forEach((_, index) => setupSingleSubscription(index));
      } catch (error) {
        if (!resolved) {
          unregisterSubscription(queryHash, cleanup);
          cleanup();
          resolved = true;
          reject(error);
        }
      }
    });
  };
};
